import { addDoc, collection, doc, getDocs, limit, orderBy, query, setDoc, writeBatch } from "firebase/firestore";
import { runtimeDb as db } from "../../firestoreStudio/runtime/runtimeDb";
import { BACKUP_COLLECTION } from "../constants";
import { serializeValue } from "../utils/firestoreValueUtils";

export async function createBackups({ collectionName, documents, adminUid, adminName, operation }) {
  const records = [];
  for (const [docId, data] of Object.entries(documents)) {
    const ref = await addDoc(collection(db, BACKUP_COLLECTION), {
      collection: collectionName,
      firestoreDocId: docId,
      data: serializeValue(data),
      adminUid,
      adminName,
      operation,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    });
    records.push(ref.id);
  }
  return records;
}

/** Faster batched backups for large delete operations (WriteBatch, default 100 per commit). */
export async function createBackupsBatched({
  collectionName,
  documents,
  adminUid,
  adminName,
  operation,
  chunkSize = 100,
  onProgress,
}) {
  const entries = Object.entries(documents);
  if (!entries.length) return [];

  const records = [];
  const createdAt = new Date().toISOString();
  const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

  for (let i = 0; i < entries.length; i += chunkSize) {
    const slice = entries.slice(i, i + chunkSize);
    const batch = writeBatch(db);
    slice.forEach(([docId, data]) => {
      const ref = doc(collection(db, BACKUP_COLLECTION));
      batch.set(ref, {
        collection: collectionName,
        firestoreDocId: docId,
        data: serializeValue(data),
        adminUid,
        adminName,
        operation,
        createdAt,
        expiresAt,
      });
      records.push(ref.id);
    });
    await batch.commit();
    onProgress?.({
      done: Math.min(i + slice.length, entries.length),
      total: entries.length,
    });
  }

  return records;
}

export async function searchBackups({ collection: col, adminUid, documentId, from, to, max = 200 }) {
  let q = query(collection(db, BACKUP_COLLECTION), orderBy("createdAt", "desc"), limit(max));
  const snap = await getDocs(q);
  let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (col) rows = rows.filter((r) => r.collection === col);
  if (adminUid) rows = rows.filter((r) => r.adminUid === adminUid);
  if (documentId) rows = rows.filter((r) => r.firestoreDocId === documentId);
  if (from) rows = rows.filter((r) => r.createdAt >= from);
  if (to) rows = rows.filter((r) => r.createdAt <= to);
  return rows;
}

export async function restoreBackupRecord(record) {
  await setDoc(doc(db, record.collection, record.firestoreDocId), record.data || {});
}

export async function deleteExpiredBackups() {
  const now = new Date().toISOString();
  const snap = await getDocs(query(collection(db, BACKUP_COLLECTION), orderBy("createdAt", "desc"), limit(500)));
  const { deleteDoc } = await import("firebase/firestore");
  let removed = 0;
  for (const d of snap.docs) {
    if ((d.data().expiresAt || "") < now) {
      await deleteDoc(d.ref);
      removed += 1;
    }
  }
  return removed;
}
